import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import CustomState from '@rio-cloud/rio-uikit/CustomState';
import { UserManager } from 'oidc-client-ts';
import { PropsWithChildren } from 'react';
import { config } from '../configuration/config';
import { environmentForLogisticsTenant } from '../configuration/login/environmentUtils';
import { LogisticsTenant } from '../configuration/tokenHandling/tokenSessionValidation';
import { isNotIframe } from '../utils';

export interface InvalidLoginProps {
    logisticsTenantFromToken: LogisticsTenant | undefined;
    userManager: UserManager;
}

export const InvalidLogin = (props: InvalidLoginProps) => {
    const onLogout = async () => {
        const userManager = props.userManager;
        const user =
            typeof (userManager as any)?.getUser === 'function' ? await userManager.getUser().catch(() => null) : null;
        const postLogoutRedirectUri = userManager?.settings?.post_logout_redirect_uri;
        await props.userManager.signoutRedirect({
            // biome-ignore lint/style/useNamingConvention: OIDC spec uses id_token_hint
            id_token_hint: user?.id_token,
            // biome-ignore lint/style/useNamingConvention: OIDC spec uses post_logout_redirect_uri
            post_logout_redirect_uri: postLogoutRedirectUri,
        });
    };

    const expectedEnvironmentBasedOnToken = environmentForLogisticsTenant(props.logisticsTenantFromToken);

    const onGoBack = () => {
        const host = window.location.host;
        const tailUrl = host.split('.').slice(1).join('.');
        const httpPrefix = host.includes('localhost') ? 'http' : 'https';
        window.location.href = `${httpPrefix}://${expectedEnvironmentBasedOnToken}.${tailUrl}`;
    };

    const environment = config.environmentFromUri?.toString() || 'unknown';

    const FormattedEnvironment = (props: { environmentString: string }) => (
        <span className={'text-uppercase text-bold'}>{props.environmentString}</span>
    );

    const currentEnvironment = <FormattedEnvironment environmentString={environment} />;
    const expectedEnvironment = <FormattedEnvironment environmentString={expectedEnvironmentBasedOnToken} />;

    const isNotRenderedInIFrame = isNotIframe();

    return (
        <InvalidLoginWrapper>
            <CustomState
                data-testid='invalid-login-message'
                icons={[
                    {
                        name: 'rioglyph-log-in',
                        color: 'text-color-light',
                        className: 'text-size-300pct',
                    },
                ]}
                headline='Login Error'
                message={
                    <div className={'text-center text-color-darker'}>
                        <p style={{ margin: 10 }}>
                            You are trying to access closing-days in the {currentEnvironment} environment.
                        </p>
                        <p style={{ margin: 10 }}>
                            You are logged into closing-days in the {expectedEnvironment} environment.
                        </p>
                        {isNotRenderedInIFrame && (
                            <>
                                Please either navigate to closing-days {expectedEnvironment} or log out in order to log
                                in to {currentEnvironment}.
                            </>
                        )}
                    </div>
                }
                buttons={
                    isNotRenderedInIFrame
                        ? [
                              {
                                  text: <span>Go to closing-days in {expectedEnvironment}</span>,
                                  bsStyle: 'default',
                                  className: 'btn btn-default',
                                  onClick: onGoBack,
                              },
                              {
                                  text: <span>Log out from {expectedEnvironment}</span>,
                                  bsStyle: 'default',
                                  className: 'btn btn-default',
                                  onClick: onLogout,
                              },
                          ]
                        : []
                }
            />
        </InvalidLoginWrapper>
    );
};

const InvalidLoginWrapper = (props: PropsWithChildren) => (
    <ApplicationLayout>
        <ApplicationLayout.Body>
            <div className={'display-flex justify-content-center height-100pct'}>
                <div className={'width-100pct align-self-center'}>
                    <div className={'padding-25 flex-column'}>{props.children}</div>
                </div>
            </div>
        </ApplicationLayout.Body>
    </ApplicationLayout>
);
